<template>
  <b-modal
    ref="refModal"
    id="taxi-account-statement-modal"
    title="Taxi Enrollment Card"
    no-close-on-backdrop
  >
    <validation-observer ref="taxiEnrollmentForm">
      <!-- First Name -->
      <validation-provider
        name="First Name"
        rules="required"
        v-slot="{ errors }"
      >
        <b-form-group label="First Name" label-for="first-name">
          <b-form-input
            id="first-name"
            v-model="juraAppMemberdetails.firstName"
            placeholder="Enter first name"
            :state="errors.length ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Last Name -->
      <validation-provider
        name="Last Name"
        rules="required"
        v-slot="{ errors }"
      >
        <b-form-group label="Last Name" label-for="last-name">
          <b-form-input
            id="last-name"
            v-model="juraAppMemberdetails.lastName"
            placeholder="Enter last name"
            :state="errors.length ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Username -->
      <validation-provider name="Username" rules="required" v-slot="{ errors }">
        <b-form-group label="Username" label-for="username">
          <b-form-input
            id="username"
            v-model="juraAppMemberdetails.username"
            placeholder="Enter username"
            :state="errors.length ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Email -->
      <validation-provider name="Email" rules="required" v-slot="{ errors }">
        <b-form-group label="Email" label-for="email">
          <b-form-input
            id="email"
            type="email"
            v-model="juraAppMemberdetails.email"
            placeholder="Enter email"
            :state="errors.length ? false : null"
            :rules="{ email: true }"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        name="Taxi Licence Number"
        rules="required"
        v-slot="{ errors }"
      >
        <b-form-group label="Taxi Licence Number" label-for="licence-number">
          <b-form-input
            id="licence-number"
            v-model="juraAppMemberdetails.taxiLicenceNum"
            placeholder="Enter licence number"
            :state="errors.length ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Taxi Licence Plate -->
      <validation-provider
        name="Taxi Licence Plate"
        rules="regex:^[A-Z0-9]*$"
        v-slot="{ errors }"
      >
        <b-form-group label="Taxi Licence Plate" label-for="licence-plate">
          <b-form-input
            id="licence-plate"
            v-model="juraAppMemberdetails.taxiRegNum"
            placeholder="Enter licence plate"
            :state="errors.length ? false : null"
          />
          <small class="text-danger">{{ errors[0] || "" }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Taxi Licence Number -->
    </validation-observer>

    <!-- VSwitch -->
    <b-form-group v-if="juraCardNum" label="Link Taxi Jura Card" label-cols="10">
      <b-form-checkbox
        v-model="juraAppMemberdetails.isActive"
        @change="taxiJuraCardExist"
        switch
      />
    </b-form-group>

    <!-- Modal Footer -->
    <template #modal-footer>
      <b-button variant="outline-secondary" @click="cancelModal"
        >Cancel</b-button
      >
      <b-button variant="primary" @click="submitForm">Save</b-button>
    </template>
  </b-modal>
</template>

<script>
import { onMounted, ref } from "@vue/composition-api";
import {
  BButton,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import useTaxiJuraApp from "../../composables/useTaxiJuraApp";

export default {
  props: {
    taxiLicenceNum: {
      type: Number,
      required: true,
    },
    juraCardNum: {
      type: String,
      required: false,
    },
  },

  setup(props) {
    const refModal = ref(null);
    const taxiEnrollmentForm = ref(null);
    const { enrolToTaxiJuraApp, getTaxiJuraApp, modifyJuraAppTaxiData } =
      useTaxiJuraApp();
    const juraAppMemberdetails = ref({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      taxiLicenceNum: "",
      taxiRegNum: "",
      isActive: false,
      juraCardNum: props.juraCardNum || null,
    });
    const originalData = ref(null);

    const taxiJuraCardExist = () => {
      if (juraAppMemberdetails.value.isActive === true) {
        juraAppMemberdetails.value.juraCardNum = props.juraCardNum;
      } else {
        juraAppMemberdetails.value.juraCardNum = null;
      }
    };

    onMounted(async () => {
      const data = await getTaxiJuraApp(props.taxiLicenceNum);
      if (data) {
        juraAppMemberdetails.value = { ...data };
        originalData.value = { ...data };

        if (data.juraCardNum) {
          juraAppMemberdetails.value.isActive = true;
        }
      }
    });

    const showModal = () => {
      refModal.value.show();
    };

    const submitForm = () => {
      taxiEnrollmentForm.value.validate().then(async (valid) => {
        if (!valid) return;

        if (juraAppMemberdetails.value.id) {
          await modifyJuraAppTaxiData(juraAppMemberdetails.value);
        } else {
          await enrolToTaxiJuraApp(juraAppMemberdetails.value);
        }

        const refreshedData = await getTaxiJuraApp(props.taxiLicenceNum);
        if (refreshedData) {
          juraAppMemberdetails.value = { ...refreshedData };
          juraAppMemberdetails.value.isActive = refreshedData.juraCardNum
            ? true
            : false;
          originalData.value = { ...refreshedData };
        }

        refModal.value.hide();
      });
    };

    const cancelModal = () => {
      juraAppMemberdetails.value = { ...originalData.value };
      refModal.value.hide();
    };

    return {
      refModal,
      taxiEnrollmentForm,
      submitForm,
      cancelModal,
      showModal,
      juraAppMemberdetails,
      taxiJuraCardExist,
    };
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    BFormCheckbox,
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
